import { Info } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Counts the mounted chart cards whose visible segments contain a "Niet
 * vastgelegd" bucket, so the page can render its one explanatory footnote
 * only when at least one does. A context rather than lifted data: each card
 * owns its own query, and this adds no requests on top.
 *
 * A count, not a set of card identities — the register/unregister effect
 * pair is what re-evaluates: a card whose bucket disappears with a new
 * period re-reports zero on the next render, and a card unmounting on a tab
 * switch removes its contribution via the effect cleanup.
 */

interface NotRecordedRegistry {
  readonly register: () => void;
  readonly unregister: () => void;
}

const NotRecordedContext = createContext<NotRecordedRegistry | null>(null);

export function NotRecordedProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [count, setCount] = useState(0);
  const registry = useMemo<NotRecordedRegistry>(
    () => ({
      register: () => setCount((current) => current + 1),
      unregister: () => setCount((current) => current - 1),
    }),
    [],
  );
  return (
    <NotRecordedContext.Provider value={registry}>
      {children}
      {count > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-border/70 bg-surface px-3 py-2.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            Niet vastgelegd betekent dat het dossier geen waarde voor dit
            onderdeel bevat. Dit wordt niet als nee geteld.
          </span>
        </div>
      ) : null}
    </NotRecordedContext.Provider>
  );
}

/**
 * Reports whether this card's rendered segments contain a "Niet vastgelegd"
 * bucket. Called by the reporting chart cards, which already build the
 * segments (so the muted bucket is visible without asking the primitive).
 * Mount/update register; unmount (tab switch) deregisters.
 * Outside a provider it does nothing, so cards stay usable standalone.
 */
export function useReportNotRecorded(hasNotRecorded: boolean): void {
  const registry = useContext(NotRecordedContext);
  useEffect(() => {
    if (!registry || !hasNotRecorded) {
      return;
    }
    registry.register();
    return registry.unregister;
  }, [registry, hasNotRecorded]);
}
